import { useEffect, useState } from 'react';
import { getArticles } from '../api';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4 },
  },
  exit: { opacity: 0, y: -20 },
};

function PublicationsList() {
  const [articles, setArticles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const fetchArticles = async () => {
      const data = await getArticles();
      const sortedArticles = [...data].sort(
        (a, b) => new Date(b.date) - new Date(a.date)
      );

      const articlesWithShowMore = sortedArticles.map((article) => ({
        ...article,
        showMore: false,
      }));

      setArticles(articlesWithShowMore);
    };
    fetchArticles();
  }, []);

  const toggleShowMore = (id) => {
    setArticles(
      articles.map((article) =>
        article.id === id ? { ...article, showMore: !article.showMore } : article
      )
    );
  };

  const filteredArticles = articles.filter((article) => {
    const title = article?.title || '';
    const term = searchTerm?.toLowerCase() || '';
    return title.toLowerCase().includes(term);
  });

  return (
    <div className="space-y-8">
      {/* Search Bar */}
      <div className="glass-card p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-surface border border-border rounded-lg flex items-center justify-center flex-shrink-0">
            <svg
              className="w-5 h-5 text-text-muted"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
              />
            </svg>
          </div>
          <input
            type="text"
            placeholder={t('publications.Search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-field flex-1"
          />
        </div>
      </div>

      {/* Section Title */}
      {searchTerm === '' && i18n.language === 'pt' && (
        <motion.h3
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-xl font-semibold text-text-primary"
        >
          ARTIGOS PUBLICADOS
        </motion.h3>
      )}

      {/* Articles List */}
      {articles.length > 0 ? (
        <AnimatePresence mode="popLayout">
          <div className="space-y-6">
            {filteredArticles.map((article, index) => (
              <motion.article
                key={article.id}
                variants={cardVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                transition={{ delay: index * 0.05 }}
                className="glass-card-hover p-6"
              >
                <h2 className="text-xl md:text-2xl font-bold text-text-primary mb-4 leading-tight">
                  {article.title}
                </h2>

                <div className="space-y-4">
                  <div>
                    <span className="inline-block text-xs font-medium text-primary uppercase tracking-wider mb-2">
                      {t('publications.Abstract')}
                    </span>
                    <p className="text-text-secondary leading-relaxed">
                      {article.resume}
                    </p>
                  </div>

                  <AnimatePresence>
                    {article.showMore && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden"
                      >
                        <p className="text-text-secondary leading-relaxed pt-4 border-t border-border">
                          {article.content}
                        </p>
                        <div className="flex items-center gap-4 mt-4">
                          {article.doi && (
                            <a
                              href={article.doi}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 text-sm text-primary hover:text-primary-light transition-colors"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                />
                              </svg>
                              DOI
                            </a>
                          )}
                          {article.pdf && (
                            <a
                              href={article.pdf}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 text-sm text-accent hover:text-accent/80 transition-colors"
                            >
                              <img src="/img/pdf.svg" alt="PDF" className="w-4 h-4" />
                              PDF
                            </a>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <button
                    onClick={() => toggleShowMore(article.id)}
                    className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary-light transition-colors group"
                  >
                    {article.showMore ? 'Ver menos' : t('publications.ForMore')}
                    <motion.svg
                      animate={{ rotate: article.showMore ? 180 : 0 }}
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 9l-7 7-7-7"
                      />
                    </motion.svg>
                  </button>
                </div>
              </motion.article>
            ))}
          </div>
        </AnimatePresence>
      ) : (
        <div className="glass-card p-12 text-center">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-primary animate-spin"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
          <p className="text-text-secondary">{t('publications.loading')}</p>
        </div>
      )}
    </div>
  );
}

export default PublicationsList;
